import { motion } from "framer-motion";
import {
  BadgeCheck,
  Brain,
  CalendarCheck,
  CheckCircle2,
  HeartHandshake,
  MapPin,
  MessageCircle,
  Phone,
  ShieldCheck,
  Sparkles,
  Star,
  Stethoscope,
  Users,
  Video,
} from "lucide-react";

const doctoraliaUrl =
  "https://www.doctoralia.com.mx/oscar-argel-gudino-montes/psiquiatra-sexologo/ciudad-de-mexico";
const phoneNumber = "5611663219";
const whatsappUrl =
  "https://wa.me/525611663219?text=Hola%20Dr.%20Oscar%2C%20me%20gustar%C3%ADa%20agendar%20una%20consulta.";
const googleMapsUrl = "https://share.google/7Gj59loZHnwS5wsja";

const heroImage =
  "https://customer-assets.emergentagent.com/job_salud-mental-segura/artifacts/gkzrodp5_WhatsApp%20Image%202026-06-03%20at%2012.53.14%20AM.jpeg";
const doctorPortrait =
  "https://customer-assets.emergentagent.com/job_salud-mental-segura/artifacts/746kriyp_image.png";
const officeGallery = [
  {
    url: "https://customer-assets.emergentagent.com/job_salud-mental-segura/artifacts/gkzrodp5_WhatsApp%20Image%202026-06-03%20at%2012.53.14%20AM.jpeg",
    title: "Consultorio cálido y privado",
    description: "Un espacio diseñado para conversar con tranquilidad y confianza.",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_salud-mental-segura/artifacts/3ucyvlqx_WhatsApp%20Image%202026-06-03%20at%2012.53.23%20AM.jpeg",
    title: "Atención profesional y cercana",
    description: "Ambiente cómodo para consulta presencial y seguimiento clínico.",
  },
];

const navItems = [
  { label: "Reseñas", href: "#resenas" },
  { label: "Sobre mí", href: "#sobre-mi" },
  { label: "Áreas", href: "#areas" },
  { label: "Consultorio", href: "#consultorio" },
  { label: "Servicios", href: "#servicios" },
  { label: "Contacto", href: "#contacto" },
];

const toTestId = (label) =>
  label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const reviews = [
  {
    title: "Un espacio seguro sin juicios ni prisas",
    quote:
      "Llevo casi un año en tratamiento y ha sido un cambio significativo. Desde la primera consulta sentí que podía hablar con confianza. No se siente como una transacción médica, sino como un espacio seguro donde puedo trabajar en mi bienestar. Lo recomiendo sin dudarlo.",
    author: "Javier H.",
  },
  {
    title: "Trato humano y atento",
    quote:
      "He estado bajo tratamiento a lo largo de mi vida con varios psiquiatras y el Dr. Oscar es el que mejor me ha tratado. La sesión inicial fue de casi dos horas, abarcamos todas mis preocupaciones. Me dio una nueva perspectiva sobre mi ansiedad y trauma.",
    author: "Mario N Castro",
  },
  {
    title: "Excelente profesional y sumamente empático",
    quote:
      "Me explicó cada paso y pregunta que realizaba. Te da la confianza de poder externar tus emociones, te escucha y sabe guiarte. 10 de 10.",
    author: "Alondra Cruz / JM",
  },
];

const academicTraining = [
  "Médico Cirujano por la Facultad de Medicina de la UNAM.",
  "Especialidad en Psiquiatría en el Hospital Psiquiátrico Fray Bernardino Álvarez.",
];

const professionalExperience = [
  "Personas que viven con VIH",
  "Personas con infecciones de transmisión sexual",
  "Personas usuarias de chemsex",
  "Personas trans",
  "Personas en PREP y otras poblaciones clave",
];

const focusAreas = [
  { icon: Brain, title: "Depresión" },
  { icon: HeartHandshake, title: "Trastornos de Ansiedad y Ataques de pánico" },
  { icon: Brain, title: "TDAH" },
  { icon: Users, title: "TEA" },
  { icon: Sparkles, title: "Trastorno Bipolar" },
  { icon: ShieldCheck, title: "Esquizofrenia" },
  { icon: Sparkles, title: "Trastorno Obsesivo Compulsivo (TOC)" },
  { icon: HeartHandshake, title: "Trastornos del sueño" },
  { icon: Users, title: "Trastornos sexuales y de pareja" },
  { icon: BadgeCheck, title: "Diversidad sexual, género y prevención (VIH/PREP)" },
  { icon: Stethoscope, title: "Atención al trauma complejo" },
  { icon: ShieldCheck, title: "Estrés, duelo y adaptación a cambios vitales" },
];

const services = [
  {
    name: "Primera consulta de Psiquiatría",
    description:
      "Evaluación profunda, historia clínica completa y diseño del plan de tratamiento inicial.",
    highlight: true,
  },
  {
    name: "Consulta de Seguimiento",
    description:
      "Monitoreo del tratamiento, ajustes necesarios y acompañamiento continuo.",
    highlight: false,
  },
];

const modalities = [
  {
    icon: MapPin,
    title: "Consulta presencial",
    text: "Atención en consultorio en un espacio privado, seguro y cómodo.",
  },
  {
    icon: Video,
    title: "Videoconsulta",
    text: "Acompañamiento profesional a distancia, con la misma dedicación y confidencialidad.",
  },
];

const sectionMotion = {
  initial: { opacity: 0, y: 34 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.22 },
  transition: { duration: 0.7, ease: "easeOut" },
};

function SectionHeader({ eyebrow, title, subtitle, testId }) {
  return (
    <div className="section-heading" data-testid={testId}>
      {eyebrow && (
        <p className="eyebrow" data-testid={`${testId}-eyebrow`}>
          {eyebrow}
        </p>
      )}
      <h2 data-testid={`${testId}-title`}>{title}</h2>
      {subtitle && <p data-testid={`${testId}-subtitle`}>{subtitle}</p>}
    </div>
  );
}

function StarRating({ testId }) {
  return (
    <div className="stars" aria-label="Calificación de cinco estrellas" data-testid={testId}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Star key={star} size={18} fill="currentColor" aria-hidden="true" />
      ))}
    </div>
  );
}

export default function LandingPage() {
  return (
    <main className="landing-page" data-testid="dr-oscar-landing-page">
      <nav className="topbar" aria-label="Navegación principal" data-testid="top-navigation">
        <a className="brand" href="#inicio" data-testid="brand-home-link">
          <span className="brand-mark" data-testid="brand-mark">OG</span>
          <span data-testid="brand-name">Dr. Oscar Gudiño</span>
        </a>
        <div className="nav-links" data-testid="navigation-links">
          {navItems.map((item) => (
            <a key={item.href} href={item.href} data-testid={`nav-link-${toTestId(item.label)}`}>
              {item.label}
            </a>
          ))}
        </div>
        <a className="nav-cta" href={doctoraliaUrl} target="_blank" rel="noreferrer" data-testid="topbar-doctoralia-cta">
          Agendar
        </a>
      </nav>

      <section id="inicio" className="hero-section" data-testid="hero-section">
        <div className="hero-orb hero-orb-one" aria-hidden="true" />
        <div className="hero-orb hero-orb-two" aria-hidden="true" />
        <div className="container hero-grid">
          <motion.div
            className="hero-copy"
            initial={{ opacity: 0, y: 36 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, ease: "easeOut" }}
          >
            <p className="eyebrow" data-testid="hero-eyebrow">
              Dr. Oscar Argel Gudiño Montes | Psiquiatría y Sexualidad
            </p>
            <h1 data-testid="hero-main-heading">
              Tu salud mental en un espacio seguro, empático y libre de juicios.
            </h1>
            <p className="hero-subtitle" data-testid="hero-subtitle">
              Recibe atención psiquiátrica integral y humanista. Te acompaño a encontrar el tratamiento adecuado para ti, explicándote cada paso a detalle y priorizando siempre tu bienestar.
            </p>
            <div className="hero-proof" data-testid="hero-social-proof">
              <StarRating testId="hero-star-rating" />
              <span>Respaldado por más de 170 opiniones de pacientes.</span>
            </div>
            <div className="hero-actions" data-testid="hero-actions">
              <a className="primary-button" href={doctoraliaUrl} target="_blank" rel="noreferrer" data-testid="hero-doctoralia-cta">
                Agendar mi primera consulta
                <CalendarCheck size={19} aria-hidden="true" />
              </a>
              <a className="secondary-button" href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="hero-whatsapp-cta">
                Escribir por WhatsApp
                <MessageCircle size={19} aria-hidden="true" />
              </a>
            </div>
            <p className="cta-note" data-testid="hero-consultation-note">
              Consultas presenciales y por videollamada.
            </p>
          </motion.div>

          <motion.div
            className="hero-visual"
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.12, ease: "easeOut" }}
            data-testid="hero-visual-card"
          >
            <img className="hero-room-image" src={heroImage} alt="Consultorio cálido y tranquilo" data-testid="hero-consultory-image" />
            <div className="hero-image-overlay" aria-hidden="true" />
            <div className="doctor-card" data-testid="doctor-profile-card">
              <img src={doctorPortrait} alt="Retrato profesional del médico" data-testid="doctor-profile-image" />
              <div>
                <p data-testid="doctor-card-title">Psiquiatría y Sexualidad</p>
                <span data-testid="doctor-card-subtitle">Atención integral, presencial o en línea</span>
              </div>
            </div>
            <div className="mode-card" data-testid="consultation-modes-card">
              <Video size={20} aria-hidden="true" />
              <span>Presencial o videoconsulta</span>
            </div>
          </motion.div>
        </div>
      </section>

      <motion.section id="resenas" className="section reviews-section" data-testid="reviews-section" {...sectionMotion}>
        <div className="container">
          <SectionHeader
            eyebrow="Confianza"
            title="Lo que mis pacientes dicen sobre la consulta"
            subtitle="Lo más valorado: Explicaciones detalladas, dedicación absoluta y puntualidad."
            testId="reviews-heading"
          />
          <div className="reviews-grid" data-testid="reviews-grid">
            {reviews.map((review, index) => (
              <article className="review-card" key={review.author} data-testid={`review-card-${index + 1}`}>
                <StarRating testId={`review-card-${index + 1}-rating`} />
                <h3 data-testid={`review-card-${index + 1}-title`}>{review.title}</h3>
                <p data-testid={`review-card-${index + 1}-quote`}>&ldquo;{review.quote}&rdquo;</p>
                <span data-testid={`review-card-${index + 1}-author`}>– {review.author}</span>
              </article>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section id="sobre-mi" className="section about-section" data-testid="about-section" {...sectionMotion}>
        <div className="container about-grid">
          <div className="about-image-wrap" data-testid="about-image-card">
            <img src={doctorPortrait} alt="Dr. Oscar Argel Gudiño Montes" data-testid="about-doctor-image" />
            <div className="credential-badge" data-testid="credential-badge">
              <ShieldCheck size={22} aria-hidden="true" />
              <span>Aval vigente por el Consejo Mexicano de Psiquiatría</span>
            </div>
          </div>
          <div className="about-copy" data-testid="about-copy">
            <SectionHeader
              eyebrow="Sobre mí"
              title="Psiquiatría, sexología y atención informada en trauma"
              testId="about-heading"
            />
            <p data-testid="about-primary-text-1">
              Soy Médico Cirujano egresado de la Facultad de Medicina de la UNAM y realicé la Especialidad en Psiquiatría en el Hospital Psiquiátrico Fray Bernardino Álvarez, uno de los centros de referencia más importantes en psiquiatría en México y América Latina.
            </p>
            <p data-testid="about-primary-text-2">
              Cuento con aval vigente por el Consejo Mexicano de Psiquiatría (Certificado: 21743276).
            </p>
            <div className="about-detail-grid" data-testid="about-detail-grid">
              <article className="about-detail-card" data-testid="about-academic-training-card">
                <h3 data-testid="about-academic-training-title">Formación académica</h3>
                <ul data-testid="about-academic-training-list">
                  {academicTraining.map((item, index) => (
                    <li key={item} data-testid={`about-academic-training-item-${index + 1}`}>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>

              <article className="about-detail-card" data-testid="about-sexology-training-card">
                <h3 data-testid="about-sexology-training-title">Formación en Sexología</h3>
                <p data-testid="about-sexology-training-text">
                  Especialidad en Sexología Educativa y Maestría en Sexología Clínica.
                </p>
              </article>

              <article className="about-detail-card" data-testid="about-professional-endorsement-card">
                <h3 data-testid="about-professional-endorsement-title">Aval profesional</h3>
                <p data-testid="about-professional-endorsement-text">
                  Avalado por el Consejo Mexicano de Psiquiatría (Certificado: 21743276).
                </p>
              </article>

              <article className="about-detail-card wide" data-testid="about-clinical-approach-card">
                <h3 data-testid="about-clinical-approach-title">Enfoque clínico</h3>
                <p data-testid="about-clinical-approach-text-1">
                  Tengo experiencia clínica y un abordaje sensible en temas de género, diversidad sexual y distintos tipos de violencia, integrando una perspectiva ética, respetuosa y libre de juicios.
                </p>
                <p data-testid="about-clinical-approach-text-2">
                  Integro una perspectiva de trauma informado basado en evidencia y centrado en la persona. Me encuentro actualmente cursando diplomados en psicotraumatología y trauma complejo en el Instituto Newman.
                </p>
              </article>

              <article className="about-detail-card wide" data-testid="about-professional-experience-card">
                <h3 data-testid="about-professional-experience-title">Experiencia profesional</h3>
                <p data-testid="about-professional-experience-intro">
                  Trabajo en la Clínica Especializada Condesa Iztapalapa, donde brindo atención integral a:
                </p>
                <ul data-testid="about-professional-experience-list">
                  {professionalExperience.map((item, index) => (
                    <li key={item} data-testid={`about-professional-experience-item-${index + 1}`}>
                      <CheckCircle2 size={18} aria-hidden="true" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <p data-testid="about-professional-experience-closing">
                  Esta experiencia fortaleció mi enfoque en salud mental desde una mirada integral, comunitaria y con perspectiva de derechos humanos.
                </p>
              </article>
            </div>
          </div>
        </div>
      </motion.section>

      <motion.section id="areas" className="section focus-section" data-testid="focus-section" {...sectionMotion}>
        <div className="container">
          <SectionHeader
            eyebrow="Áreas de enfoque"
            title="¿En qué te puedo ayudar?"
            subtitle="Atención especializada para adultos a través de tratamientos personalizados y contemporáneos."
            testId="focus-heading"
          />
          <div className="focus-grid" data-testid="focus-grid">
            {focusAreas.map((area, index) => {
              const Icon = area.icon;
              return (
                <article className="focus-card" key={area.title} data-testid={`focus-area-card-${index + 1}`}>
                  <div className="focus-icon" data-testid={`focus-area-card-${index + 1}-icon`}>
                    <Icon size={24} aria-hidden="true" />
                  </div>
                  <h3 data-testid={`focus-area-card-${index + 1}-title`}>{area.title}</h3>
                </article>
              );
            })}
          </div>
        </div>
      </motion.section>

      <motion.section id="consultorio" className="section office-section" data-testid="office-section" {...sectionMotion}>
        <div className="container">
          <SectionHeader
            eyebrow="Consultorio"
            title="Un espacio pensado para sentirte en confianza"
            subtitle="El entorno de consulta está diseñado para favorecer la privacidad, la escucha y una experiencia cómoda desde el primer encuentro."
            testId="office-heading"
          />
          <div className="office-gallery" data-testid="office-gallery">
            {officeGallery.map((image, index) => (
              <article className="office-card" key={image.url} data-testid={`office-card-${index + 1}`}>
                <img src={image.url} alt={image.title} data-testid={`office-card-${index + 1}-image`} />
                <div className="office-card-caption" data-testid={`office-card-${index + 1}-caption`}>
                  <h3 data-testid={`office-card-${index + 1}-title`}>{image.title}</h3>
                  <p data-testid={`office-card-${index + 1}-description`}>{image.description}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </motion.section>

      <motion.section id="servicios" className="section services-section" data-testid="services-section" {...sectionMotion}>
        <div className="container services-grid">
          <div className="services-intro" data-testid="services-intro">
            <SectionHeader
              eyebrow="Servicios"
              title="Servicios y Modalidades"
              subtitle="Elige la modalidad que mejor se adapte a ti. Ofrezco consultas tanto de forma presencial como mediante videoconsulta."
              testId="services-heading"
            />
            <div className="contact-info-box" data-testid="service-contact-info-notice">
              <MessageCircle size={22} aria-hidden="true" />
              <p>
                Para más información, agenda o envía un mensaje por los medios de contacto disponibles: Doctoralia, WhatsApp o teléfono.
              </p>
            </div>
          </div>
          <div className="service-cards" data-testid="service-cards">
            <div className="modality-cards" data-testid="modality-cards">
              {modalities.map((modality, index) => {
                const Icon = modality.icon;
                return (
                  <article className="modality-card" key={modality.title} data-testid={`modality-card-${index + 1}`}>
                    <div className="modality-icon" data-testid={`modality-card-${index + 1}-icon`}>
                      <Icon size={23} aria-hidden="true" />
                    </div>
                    <h3 data-testid={`modality-card-${index + 1}-title`}>{modality.title}</h3>
                    <p data-testid={`modality-card-${index + 1}-text`}>{modality.text}</p>
                  </article>
                );
              })}
            </div>
            {services.map((service, index) => (
              <article className={`service-card ${service.highlight ? "highlight" : ""}`} key={service.name} data-testid={`service-card-${index + 1}`}>
                {service.highlight && <span className="service-tag" data-testid="primary-service-tag">Evaluación inicial</span>}
                <h3 data-testid={`service-card-${index + 1}-name`}>{service.name}</h3>
                <p data-testid={`service-card-${index + 1}-description`}>{service.description}</p>
              </article>
            ))}
          </div>
        </div>
      </motion.section>

      <section id="contacto" className="final-cta-section" data-testid="contact-section">
        <div className="container final-cta-card" data-testid="final-cta-card">
          <div className="final-copy" data-testid="final-cta-copy">
            <p className="eyebrow" data-testid="final-cta-eyebrow">Agenda tu consulta</p>
            <h2 data-testid="final-cta-title">Da el primer paso hacia tu bienestar</h2>
            <p data-testid="final-cta-text">
              Estoy aquí para escucharte y encontrar juntos el mejor camino para tu salud mental. Revisa mi calendario en línea y elige el horario que mejor te convenga.
            </p>
            <p className="important-note" data-testid="final-cta-note">
              ¿No encuentras un horario disponible que se ajuste a tu rutina? Envíame un mensaje directo a través de la plataforma y con gusto buscaremos una alternativa. ¡Será un placer atenderte!
            </p>
            <div className="final-actions" data-testid="final-actions">
              <a className="primary-button light" href={doctoraliaUrl} target="_blank" rel="noreferrer" data-testid="final-doctoralia-cta">
                Ver disponibilidad y agendar cita
                <CalendarCheck size={19} aria-hidden="true" />
              </a>
              <a className="secondary-button light" href={whatsappUrl} target="_blank" rel="noreferrer" data-testid="final-whatsapp-cta">
                WhatsApp
                <MessageCircle size={19} aria-hidden="true" />
              </a>
              <a className="maps-button" href={googleMapsUrl} target="_blank" rel="noreferrer" data-testid="final-google-maps-cta">
                Ver ubicación en Google Maps
                <MapPin size={19} aria-hidden="true" />
              </a>
            </div>
          </div>
          <div className="contact-panel" data-testid="contact-info-panel">
            <div className="contact-row" data-testid="contact-address">
              <MapPin size={22} aria-hidden="true" />
              <span>S. Lorenzo 153, Tlacoquemecatl del Valle, Benito Juárez, 03100 Ciudad de México, CDMX</span>
            </div>
            <a className="contact-row" href={`tel:+52${phoneNumber}`} data-testid="contact-phone-link">
              <Phone size={22} aria-hidden="true" />
              <span>{phoneNumber}</span>
            </a>
            <a className="contact-row" href={doctoraliaUrl} target="_blank" rel="noreferrer" data-testid="contact-doctoralia-link">
              <CalendarCheck size={22} aria-hidden="true" />
              <span>Perfil y calendario en Doctoralia</span>
            </a>
            <a className="contact-row" href={googleMapsUrl} target="_blank" rel="noreferrer" data-testid="contact-google-maps-link">
              <MapPin size={22} aria-hidden="true" />
              <span>Abrir ubicación en Google Maps</span>
            </a>
          </div>
        </div>
      </section>

      <a
        className="floating-whatsapp-button"
        href={whatsappUrl}
        target="_blank"
        rel="noreferrer"
        aria-label="Enviar mensaje por WhatsApp"
        data-testid="floating-whatsapp-button"
      >
        <MessageCircle size={24} aria-hidden="true" />
        <span>WhatsApp</span>
      </a>
    </main>
  );
}
